import { Modal } from "../common/Modal";
import { useState, useEffect } from "react";
import { getOnPremPimsAppointmentTypes } from "../../serverActions/onPremPimsApis";
import { useAuth } from "@clerk/clerk-react";
import { Spinner } from "../../utils/Spinner";

interface SyncSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    pimsDisplayName: string;
}

interface AppointmentTypeLocalObject {
    id: string;
    name: string;
    selected: boolean;
}

interface AppointmentTypeResponse {
    id: string;
    name: string;
    length: number;
}

interface GetOnPremPimsAppointmentTypesResponse {
    appointment_types: AppointmentTypeResponse[];
}

const STORAGE_KEY = "syncAppointmentTypes";

export default function SyncSettingsModal({ isOpen, onClose, pimsDisplayName }: SyncSettingsModalProps) {
    const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeLocalObject[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { getToken } = useAuth();

    const allSelected = appointmentTypes.every(type => type.selected);
    const someSelected = appointmentTypes.some(type => type.selected);

    // Load settings from localStorage on mount and fetch real types
    useEffect(() => {
        if (isOpen) {
            // First, load from localStorage so we have something immediately
            const loadFromLocalStorage = () => {
                const savedTypes = localStorage.getItem(STORAGE_KEY);
                if (savedTypes) {
                    try {
                        const parsed = JSON.parse(savedTypes);
                        if (Array.isArray(parsed)) {
                            setAppointmentTypes(parsed);
                            return parsed;
                        }
                    } catch (e) {
                        console.error("Error parsing sync settings:", e);
                        // If there's an error, reset to defaults
                        localStorage.removeItem(STORAGE_KEY);
                    }
                }
                return null;
            };

            // Then fetch from API
            const fetchAppointmentTypes = async () => {
                setLoading(true);
                setError(null);
                try {
                    const token = await getToken({ template: "supabase" }) ?? "";
                    const response = await getOnPremPimsAppointmentTypes({ token: token }) as GetOnPremPimsAppointmentTypesResponse;
                    
                    if (response && response.appointment_types) {
                        // Get stored selection state
                        const savedTypes = loadFromLocalStorage();
                        const selectionMap = new Map<string, boolean>();
                        
                        if (savedTypes) {
                            // Create a map of id -> selected state from localStorage
                            savedTypes.forEach((type: AppointmentTypeLocalObject) => {
                                selectionMap.set(type.id, type.selected);
                            });
                        }
                        
                        // Create new array with real appointment types, preserving selection state
                        const newTypes = response.appointment_types.map((type: AppointmentTypeResponse) => ({
                            id: type.id,
                            name: type.name,
                            // If we have a saved selection state, use it, otherwise default to true
                            selected: selectionMap.has(type.id) 
                                ? selectionMap.get(type.id) 
                                : true
                        }));
                        
                        setAppointmentTypes(newTypes as AppointmentTypeLocalObject[]);
                        
                        // Save the updated list to localStorage
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(newTypes));
                    }
                } catch (e) {
                    console.error("Error fetching appointment types:", e);
                    setError("Failed to load appointment types. Please try again later.");
                    
                    // Don't set default types, just show error message
                    if (!loadFromLocalStorage()) {
                        setAppointmentTypes([]);
                    }
                } finally {
                    setLoading(false);
                }
            };
            
            fetchAppointmentTypes();
        }
    }, [isOpen, getToken]);

    const handleToggleType = (id: string) => {
        const updatedTypes = appointmentTypes.map(type => 
            type.id === id ? { ...type, selected: !type.selected } : type
        );
        setAppointmentTypes(updatedTypes);
    };

    const handleToggleAll = () => {
        // If all are selected, unselect all
        // If some or none are selected, select all
        const newValue = !allSelected;
        const updatedTypes = appointmentTypes.map(type => ({ 
            ...type, 
            selected: newValue 
        }));
        setAppointmentTypes(updatedTypes);
    };

    const handleSave = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(appointmentTypes));
        onClose();
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose}
            title={`${pimsDisplayName} Sync Settings`}
            maxWidth="sm:max-w-md"
        >
            <div className="mt-4">
                <p className="text-sm text-gray-500 mb-4">
                    Select which appointment types to sync from {pimsDisplayName}:
                </p>
                
                {error && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm">
                        {error}
                    </div>
                )}
                
                <div className="border-b border-gray-200 pb-2 mb-3">
                    <div className="flex items-center">
                        <input
                            id="select-all"
                            name="select-all"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={allSelected}
                            onChange={handleToggleAll}
                            disabled={loading}
                            ref={input => {
                                if (input) {
                                    input.indeterminate = someSelected && !allSelected;
                                }
                            }}
                        />
                        <label htmlFor="select-all" className="ml-2 block text-sm font-medium text-gray-900">
                            Select All Appointment Types
                        </label>
                    </div>
                </div>
                
                <div className="max-h-60 overflow-y-auto">
                    {loading ? (
                        <div className="py-8 text-center flex flex-col items-center justify-center">
                            <Spinner size="w-8 h-8" timer={false} />
                            <p className="text-gray-500 mt-2">Loading appointment types...</p>
                        </div>
                    ) : appointmentTypes.length === 0 ? (
                        <div className="py-4 text-center text-gray-500">
                            {error ? "No appointment types available." : "No appointment types available."}
                        </div>
                    ) : (
                        appointmentTypes.map((type) => (
                            <div key={type.id} className="flex items-center py-2 hover:bg-gray-50 px-2 rounded">
                                <input
                                    id={`type-${type.id}`}
                                    name={`type-${type.id}`}
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={type.selected}
                                    onChange={() => handleToggleType(type.id)}
                                />
                                <label htmlFor={`type-${type.id}`} className="ml-2 block text-sm text-gray-900">
                                    {type.name}
                                </label>
                            </div>
                        ))
                    )}
                </div>
                
                <div className="mt-4 text-xs text-gray-500 bg-blue-50 p-3 rounded-md">
                    <p className="font-medium text-blue-800 mb-1">About Appointment Types</p>
                    <p>Only appointments with the selected types will be synced from {pimsDisplayName}. 
                    Your selection is saved for future syncs.</p>
                    {allSelected && (
                        <p className="mt-2 font-medium text-blue-700">
                            All appointment types are currently selected. This will sync all appointments without filtering.
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-x-3">
                <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    onClick={handleSave}
                    disabled={loading}
                >
                    Save
                </button>
            </div>
        </Modal>
    );
} 